import { useEffect, useRef } from "react";
import Resumable from "resumablejs";
import { ALLOWED_FILE_EXTENSION } from "../../constants";
import { WorkerUploadEngine, ChunkInfoItem } from "./workerUploadEngine";

// When on, chunk uploads run in a Web Worker so a backgrounded tab doesn't
// throttle them. Falsy keeps the legacy main-thread resumablejs path untouched.
const USE_UPLOAD_WORKER = !!process.env.NX_APP_UPLOAD_WORKER;

// The bridge (WorkerUploadEngine) implements the same surface index.tsx uses
// (.on / .files / .opts / .upload / .assignDrop / .assignBrowse), so it's cast
// to Resumable at construction and the handler code below is engine-agnostic.

interface IUploader {
  id: string;
  children?: JSX.Element;
  onFileEvent: (event: string, data: any, cb?: any) => any;
  datatype: string;
  size?: string;
  updateIsThumbnailUploading?: any;
  isThumbnailUploading?: any;
  disabled?: boolean;
  type?: string;
  width?: string;
  metaPreset?: string;
  socialPreset?: string;
}

const resumableFiles: Record<string, any> = {};

export { resumableFiles };

export const removeFileFromResumableFiles = (uniqueIdentifier: string) => {
  delete resumableFiles[uniqueIdentifier];
};

// Shared base config for every Resumable instance in the app (Uploader's
// drop-zones and BulkUploadForm's per-file instances) so upload tuning
// (chunk size, retry policy, …) lives in one place instead of being copied.
export const createResumableConfig = (
  overrides: Partial<Resumable.ConfigurationHash> = {}
): Resumable.ConfigurationHash =>
  ({
    target: process.env.NX_APP_UPLOAD ?? "",
    query: { method: "asset.resumable" },
    chunkSize: 4 * 1024 * 1024,
    simultaneousUploads: 3,
    testChunks: false,
    maxChunkRetries: 50,
    chunkRetryInterval: 2000,
    // Auth failures don't recover on retry; without this a token expiring
    // mid-upload burns maxChunkRetries × chunkRetryInterval per chunk.
    permanentErrors: [400, 401, 403, 404, 415, 500, 501],
    // No-op unless addFileToUploadEngine was called with uploadedChunks (reupload).
    preprocess: skipAlreadyUploadedChunks,
    ...overrides,
  }) as unknown as Resumable.ConfigurationHash;

// Identifiers stashed by addFileToUploadEngine just before resumable.addFile()
// so the Resumable-engine branch's generateUniqueIdentifier (below) can hand
// back the caller's precomputed identifier instead of resumablejs' default
// (size + sanitized name), keeping both engines' identifiers identical.
const pendingIdentifiers = new WeakMap<File, string>();

// Reupload only: chunks the server already has for a given file identifier
// (from asset.chunkinfo), consulted by the resumablejs `preprocess` hook below
// to skip re-sending them. Populated by addFileToUploadEngine just before
// addFile()/upload(); every other call site never writes to this map, so the
// preprocess hook is a no-op (falls through to the real send) for them.
const pendingUploadedChunks = new Map<string, ChunkInfoItem[]>();

// Reupload call sites should call this on fileSuccess/fileError so a stale
// entry can't leak into a later upload that happens to reuse the identifier.
export const clearPendingUploadedChunks = (uniqueIdentifier: string) => {
  pendingUploadedChunks.delete(uniqueIdentifier);
};

// resumablejs has no built-in "mark this chunk as already uploaded" — status()
// is derived purely from chunk.xhr. Fake a completed XHR so status()/progress()
// naturally read success, then drive the same completion path doneHandler
// would (resumable.js:707-711), and hand control back to the queue instead of
// calling the real send().
const skipAlreadyUploadedChunks = (chunk: any) => {
  const uploadedChunks = pendingUploadedChunks.get(
    chunk.fileObj?.uniqueIdentifier
  );
  const chunkSize = chunk.endByte - chunk.startByte;
  const alreadyUploaded = uploadedChunks?.some(
    (c) => c.number === chunk.offset + 1 && c.size === chunkSize
  );

  if (!alreadyUploaded) {
    chunk.preprocessFinished();
    return;
  }

  chunk.xhr = { readyState: 4, status: 200, responseText: "" };
  chunk.preprocessState = 2;
  chunk.callback("progress");
  chunk.callback("success", "");
  chunk.resumableObj.uploadNextChunk();
};

// Same engine choice as the Uploader drop-zone below, exposed for call sites
// (RenderAction's reupload, BulkUploadForm) that build their own per-file
// Resumable/WorkerUploadEngine instance instead of using <Uploader>. Cast to
// Resumable so the rest of those call sites' handler code stays engine-agnostic.
export const createUploadEngine = (
  overrides: Partial<Resumable.ConfigurationHash> = {}
): Resumable => {
  const config = createResumableConfig({
    ...overrides,
    // Bundled resumable.d.ts omits this option but the lib supports it.
    ...({
      generateUniqueIdentifier: (file: File) =>
        pendingIdentifiers.get(file) ??
        `${file.size}-${file.name.replace(/[^0-9a-zA-Z_-]/gim, "")}`,
    } as any),
  });
  if (!USE_UPLOAD_WORKER) return new Resumable(config) as Resumable;

  const cfg = config as unknown as Record<string, any>;
  return new WorkerUploadEngine({
    target: cfg.target,
    query: cfg.query,
    headers: cfg.headers,
    chunkSize: cfg.chunkSize,
    simultaneousUploads: cfg.simultaneousUploads,
    maxChunkRetries: cfg.maxChunkRetries,
    chunkRetryInterval: cfg.chunkRetryInterval,
    permanentErrors: cfg.permanentErrors,
  }) as unknown as Resumable;
};

// Programmatic addFile for call sites driving upload from a hidden
// <input type="file"> instead of the drop-zone/browse flow — mirrors
// resumablejs' `resumable.addFile(file)` on either engine, using the caller's
// precomputed identifier so it lines up with the Redux upload-progress key.
//
// uploadedChunks (reupload only): chunks the server already has for this
// upload_id (from asset.chunkinfo), so they're skipped instead of re-sent.
// Omitted by every other call site.
export const addFileToUploadEngine = (
  engine: Resumable,
  file: File,
  uniqueIdentifier: string,
  uploadedChunks?: ChunkInfoItem[]
): void => {
  if (USE_UPLOAD_WORKER) {
    // uploadedChunks must reach WorkerFile before fire("fileAdded") — see
    // addFileWithIdentifier's comment — so it's passed in, not set afterward.
    (engine as unknown as WorkerUploadEngine).addFileWithIdentifier(
      file,
      uniqueIdentifier,
      uploadedChunks
    );
    return;
  }
  if (uploadedChunks?.length) {
    pendingUploadedChunks.set(uniqueIdentifier, uploadedChunks);
  }
  pendingIdentifiers.set(file, uniqueIdentifier);
  engine.addFile(file);
  pendingIdentifiers.delete(file);
};

export const FILE_CHANGED_MESSAGE =
  "This file changed or is still being written on disk. Make sure the file is fully saved (e.g. recording finished), then re-select it.";

const REPLACED_MESSAGE =
  "Upload cancelled because a new file was selected in its place.";

const GENERIC_ERROR_MESSAGE = "Error While Uploading";

// cancel() throws if the file was already removed from the queue.
export const safeCancel = (file: any) => {
  try {
    file.cancel();
  } catch {
    /* noop */
  }
};

// resumable.js passes the raw server response body as the fileError message.
// Reduce it to something safe to show a user: keep plain-text messages,
// extract message/error fields from JSON bodies, and drop HTML error pages.
// Consumers fall back to a generic message when this returns "".
export const normalizeUploadError = (message: unknown): string => {
  if (typeof message !== "string") return "";
  const text = message.trim();
  if (!text || text.startsWith("<")) return "";
  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      const msg = parsed?.message || parsed?.error || parsed?.msg;
      return typeof msg === "string" ? msg : "";
    } catch {
      return "";
    }
  }
  return text;
};

// Chromium aborts a chunk read with net::ERR_UPLOAD_FILE_CHANGED when the file's
// size/lastModified on disk no longer match the snapshot taken when it was picked.
// The read rejects instead of returning bytes; detect it before the upload starts
// (and again on failure) so we can surface a clear message instead of letting
// resumable.js grind through maxChunkRetries useless attempts per chunk.
export const isFileReadable = (rawFile: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!rawFile || typeof rawFile.slice !== "function") {
      resolve(false);
      return;
    }
    try {
      // Read the tail slice — a growing/truncated file fails here even when the head still reads.
      const start = Math.max(0, rawFile.size - 1);
      const blob = rawFile.slice(start, rawFile.size);
      const reader = new FileReader();
      reader.onload = () => resolve(true);
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(blob);
    } catch {
      resolve(false);
    }
  });
};

export const Uploader = ({
  id,
  children,
  onFileEvent,
  datatype,
  size,
  updateIsThumbnailUploading,
  isThumbnailUploading,
  disabled,
  type,
  width,
  metaPreset,
  socialPreset,
}: IUploader) => {
  const dropZoneRef = useRef<HTMLDivElement | null>(null);
  const resumableRef = useRef<Resumable | null>(null);

  // Event handlers are registered on the Resumable instance exactly once, so
  // they must read props through this ref — capturing them directly would
  // freeze the mount-time values (onFileEvent identity, preset selections, …).
  const latestProps = {
    id,
    type,
    metaPreset,
    socialPreset,
    onFileEvent,
    updateIsThumbnailUploading,
  };
  const propsRef = useRef(latestProps);
  propsRef.current = latestProps;

  useEffect(() => {
    if (!dropZoneRef.current) return;

    // Initialize the upload engine only if it hasn't been initialized yet
    if (!resumableRef.current) {
      let fileSeq = 0;
      // The default identifier is size + the [0-9a-zA-Z_-] chars of the name,
      // so non-Latin filenames all collapse to "<size>-" and the engine would
      // silently skip any file whose identifier it has already seen. The
      // per-session suffix keeps identifiers unique.
      const generateUniqueIdentifier = (file: any) => {
        const name =
          file.webkitRelativePath || file.fileName || file.name || "";
        const sanitized = name.replace(/[^0-9a-zA-Z_-]/gim, "");
        return `${file.size}-${sanitized}-${Date.now().toString(36)}${++fileSeq}`;
      };

      // Same config for both engines. Auth failures don't recover on retry, so
      // permanentErrors short-circuits a token expiring mid-upload (otherwise it
      // burns maxChunkRetries × chunkRetryInterval per chunk).
      const engineConfig = {
        target: process.env.NX_APP_UPLOAD ?? "",
        query: { method: "asset.resumable" },
        chunkSize: 4 * 1024 * 1024,
        simultaneousUploads: 3,
        maxChunkRetries: 50,
        chunkRetryInterval: 2000,
        permanentErrors: [400, 401, 403, 404, 415, 500, 501],
        generateUniqueIdentifier,
      };

      // The bridge implements the same surface the handlers below use, so the
      // rest of this effect is engine-agnostic. Cast to Resumable for the
      // shared handler code (the two on()-signatures otherwise intersect).
      resumableRef.current = USE_UPLOAD_WORKER
        ? (new WorkerUploadEngine(engineConfig) as unknown as Resumable)
        : (new Resumable({
            ...engineConfig,
            testChunks: false,
            // maxFiles:1 replacement is handled by the fileAdded guard below;
            // the worker path enforces the same via activeFile bookkeeping.
            maxFiles: 1,
            // permanentErrors and the generateUniqueIdentifier signature are
            // missing from the bundled resumable.d.ts but supported by the lib.
          } as unknown as Resumable.ConfigurationHash) as Resumable);

      // Consecutive chunk-retry count per file, used by the mid-upload
      // file-changed guard on the "fileRetry" handler below.
      const retryCounts: Record<string, number> = {};
      const RETRY_THRESHOLD = 5;

      // Files a fileError has already been emitted for — resumable.js can
      // report a file failed more than once (e.g. our synthetic error followed
      // by its own), and consumers decrement counters on every fileError.
      const erroredFiles = new Set<string>();

      // The file this uploader is currently handling. With maxFiles: 1,
      // resumable.js silently removes the previous file when a new one is
      // picked, so we have to clean up after it ourselves.
      let activeFile: Resumable.ResumableFile | null = null;

      // Single teardown path for per-file bookkeeping; every settled file
      // (success, error, replacement) must come through here.
      const releaseFile = (file: any) => {
        const key = file?.uniqueIdentifier;
        if (key) {
          removeFileFromResumableFiles(key);
          delete retryCounts[key];
        }
        if (activeFile === file) activeFile = null;
      };

      // Guarantees consumers a non-empty, user-safe error string — they can
      // toast/store it as-is without re-guarding.
      const emitFileError = (file: any, error: string) => {
        const key = file?.uniqueIdentifier;
        if (key) {
          if (erroredFiles.has(key)) return;
          erroredFiles.add(key);
        }
        releaseFile(file);
        const { onFileEvent, updateIsThumbnailUploading } = propsRef.current;
        updateIsThumbnailUploading?.(false);
        onFileEvent("fileError", {
          file: file,
          error: error || GENERIC_ERROR_MESSAGE,
        });
      };

      resumableRef.current.on("fileAdded", (file) => {
        const { id, type, metaPreset, socialPreset, onFileEvent } =
          propsRef.current;

        // Identifiers are unique per session, so entries for files before the
        // one being replaced below can never match again — drop them instead
        // of accumulating.
        erroredFiles.clear();

        // If the previous file was still uploading, adding this one silently
        // killed its upload (maxFiles: 1 replacement) — surface that so its
        // progress entry and the upload counter don't leak. A file no longer
        // in resumableFiles was already cancelled externally (UploadProgress)
        // and only needs its bookkeeping released.
        if (activeFile && activeFile !== file) {
          const replaced = activeFile;
          const stillTracked =
            resumableFiles[replaced.uniqueIdentifier] === replaced;
          if (stillTracked && !replaced.isComplete()) {
            emitFileError(replaced, REPLACED_MESSAGE);
          } else {
            releaseFile(replaced);
          }
        }

        if (
          !file.fileName.toLowerCase().endsWith(".vtt") &&
          id === "subtitle"
        ) {
          // Drop it from resumable's queue too, or a later upload() would send it.
          safeCancel(file);
          emitFileError(file, "Only .vtt files are allowed");
          return;
        }

        activeFile = file;
        //Add file to resumableFiles
        resumableFiles[file.uniqueIdentifier] = file;

        onFileEvent(
          "fileAdded",
          {
            file,
            isShorts: id === "shorts",
            asset_type: type?.toLowerCase(),
            id: id,
            type: type,
            metaPreset: metaPreset,
            socialPreset: socialPreset,
          },
          async (query: any, token: string) => {
            const resumable = resumableRef.current;
            if (!query || !resumable) {
              console.error("No query returned in the onFileEvent callback.");
              return;
            }

            // Registration is async — the file may have been replaced or
            // cancelled meanwhile. upload() sends whatever is queued, so
            // starting it now would upload a different file under this
            // file's entity/token.
            if (!resumable.files.includes(file)) return;

            // Preflight: verify the file is still readable before starting.
            // Catches recordings/files that changed on disk after being picked,
            // so we fail fast with a clear message instead of a retry storm.
            const readable = await isFileReadable(file.file);
            if (!readable) {
              safeCancel(file);
              emitFileError(file, FILE_CHANGED_MESSAGE);
              return;
            }
            if (!resumable.files.includes(file)) return;

            resumable.opts.query = {
              ...resumable.opts.query,
              ...query,
            };

            resumable.opts.headers = {
              token: token,
            };

            resumable.upload();
          }
        );
      });

      resumableRef.current.on("fileSuccess", (file: any, message: any) => {
        releaseFile(file);
        const { onFileEvent, updateIsThumbnailUploading } = propsRef.current;
        updateIsThumbnailUploading?.(false);
        onFileEvent("fileSuccess", {
          fileName: file.fileName,
          id: file.uniqueIdentifier,
          response: message,
        });
      });

      resumableRef.current.on("fileError", (file, message) => {
        emitFileError(file, normalizeUploadError(message));
      });

      resumableRef.current.on("fileProgress", (file) => {
        propsRef.current.onFileEvent("fileProgress", {
          file: file,
        });
      });

      // Runtime guard: if the file changes on disk mid-upload, chunk reads start
      // failing (net::ERR_UPLOAD_FILE_CHANGED, XHR status 0). resumable.js treats
      // this as a transient network error and retries up to maxChunkRetries (50)
      // per chunk, which is pointless — the file won't un-change. After a few
      // consecutive retries on a file, re-verify readability and abort with a
      // clear message instead of grinding through the retry storm.
      resumableRef.current.on("fileRetry", async (file: any) => {
        const key = file.uniqueIdentifier;
        retryCounts[key] = (retryCounts[key] || 0) + 1;
        if (retryCounts[key] < RETRY_THRESHOLD) return;

        const readable = await isFileReadable(file.file);
        if (!readable) {
          // cancel(), not abort(): abort only pauses in-flight chunks and
          // leaves the file queued, so the retry loop picks it right back up.
          safeCancel(file);
          emitFileError(file, FILE_CHANGED_MESSAGE);
        }
      });

      resumableRef.current.assignDrop(dropZoneRef.current);
      resumableRef.current.assignBrowse(dropZoneRef.current, false);
    }

    // Keep the browse input's accept filter in sync with the datatype.
    const inputElements =
      dropZoneRef.current.querySelectorAll('input[type="file"]');
    const allowedFileTypes: string[] =
      ALLOWED_FILE_EXTENSION[datatype.toUpperCase()] ||
      ALLOWED_FILE_EXTENSION["ALL"];
    const fileTypes = allowedFileTypes.map((type) => `.${type}`).join(",");
    inputElements.forEach((inputElement) => {
      (inputElement as HTMLInputElement).setAttribute("accept", fileTypes);
    });
  }, [id, datatype]);

  return (
    <div
      style={{
        width: width || size,
        height: size,
        pointerEvents: isThumbnailUploading ? "none" : "auto",
        opacity: isThumbnailUploading ? 0.7 : 1,
        cursor: isThumbnailUploading ? "not-allowed" : "pointer",
        ...(disabled && {
          opacity: 0.7,
          pointerEvents: "none",
          cursor: "not-allowed",
        }),
      }}
      ref={dropZoneRef}
    >
      {children}
    </div>
  );
};
